import { Chain, Network } from "@webapp/shared";
import { sortBy } from "lodash";
import { ProtoChain, ProtoNetwork } from "./protobufs";
// eslint-disable-next-line import/no-cycle -- Ignoring all legacy import cycles
import { WaffleFlag } from "@queries/useWaffleFlags";
import { IS_PRODUCTION } from "./deployEnv";

export { Chain, Network };

export const FREE_TIER_CU_LIMIT = 300000000;
export const GROWTH_TIER_CU_LIMIT = 400000000;
export const GROWTH_TIER_MONTHLY_BASE_PRICE = 49;
export const GROWTH_TIER_CU_PRICE_PER_CU = 0.0000012;

export const FREE_TIER_CUS_LABEL = FREE_TIER_CU_LIMIT.toLocaleString();
export const GROWTH_TIER_CUS_LABEL = GROWTH_TIER_CU_LIMIT.toLocaleString();

// used on SignUp app, SignupShareView.tsx
export const REINFORCED_TRANSACTIONS_COST_PER_APP = 999;

// Deprecated
export type NetworkEnum = ProtoNetwork;

// Deprecated
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- FIXME
export const protoNetworkToName: Record<ProtoNetwork, string> =
  Object.fromEntries(Object.entries(ProtoNetwork).map((a) => a.reverse()));

// Deprecated
export function ethNetworkToProtoNetworkEnum(network: Network): ProtoNetwork {
  return NETWORK_INFO[network].protoNetwork;
}

const API_DOMAIN_SUFFIX = IS_PRODUCTION ? "alchemy.com" : "alchemypreview.com";

export enum SubscriptionTier {
  FREE = 0,
  GROWTH = 1,
  ENTERPRISE = 2,
  OTHER = 3,
  SCALE = 4,
}

export enum PlanTerm {
  MONTHLY = 0,
  ANNUAL = 1,
}

export enum UsageCapType {
  AUTO_SCALE = 0,
  CAPPED = 1,
}

export enum WebhookType {
  DROPPED_TRANSACTION = 1,
  MINED_TRANSACTION = 2,
  ADDRESS_ACTIVITY = 4,
  NFT_ACTIVITY = 6,
  NFT_METADATA_UPDATE = 7, // Deprecated for new networks
  GRAPHQL = 8,

  // Not supported
  PENDING_TRANSACTION = 0,
  DELAYED_TRANSACTION = 3,
  GAS_PRICE = 5,
}

const CHAIN_SORT_ORDER = [
  Chain.ETH,
  Chain.POLYGON,
  Chain.ARB,
  Chain.OPT,
  Chain.POLYGONZKEVM,
  Chain.BASE,
  Chain.STARKNET,
  Chain.SOLANA,
  Chain.ASTAR,
  Chain.ZKSYNC,
  Chain.ZORA,
  Chain.FRAX,
  Chain.BNB,
  Chain.AVAX,
  Chain.ARBNOVA,
];

export const CHAIN_INFO: Record<
  Chain,
  {
    // Chain enum value used by frontend
    chain: Chain;
    // Blockchain enum value used in protobufs
    value: ProtoChain;
    // Display name
    name: string;
    // All networks that make up this chain, even if not public or deprecated
    networks: Network[];
    // Network that is chosen for this chain by default
    defaultNetwork: Network;
    // Currency used to denote gas & transaction value
    currency: string;
    // Transactions show in the mempool
    supportsMempool: boolean;
    // Apps with this chain support our reinforced transactions addon
    supportsReinforcedTxns: boolean;
    // Apps with this chain support websockets
    supportsWebsockets: boolean;
    // API for this chain is limited to only AA methods
    supportsOnlyAccountAbstraction?: boolean;
    // URL to the alchemy docs;
    docsUrl: string;
    // Order to display the chain in selectors
    order: number;
  }
> = {
  [Chain.ETH]: {
    chain: Chain.ETH,
    value: ProtoChain.ETH,
    name: "Ethereum",
    networks: [
      Network.ETH_MAINNET,
      Network.ETH_ROPSTEN,
      Network.ETH_RINKEBY,
      Network.ETH_KOVAN,
      Network.ETH_GOERLI,
      Network.ETH_SEPOLIA,
      Network.ETH_HOLESKY,
    ],
    defaultNetwork: Network.ETH_MAINNET,
    currency: "ETH",
    supportsMempool: true,
    supportsReinforcedTxns: true,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/ethereum-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.ETH),
  },
  [Chain.ARB]: {
    chain: Chain.ARB,
    value: ProtoChain.ARB,
    name: "Arbitrum",
    networks: [
      Network.ARB_MAINNET,
      Network.ARB_RINKEBY,
      Network.ARB_GOERLI,
      Network.ARB_SEPOLIA,
    ],
    defaultNetwork: Network.ARB_MAINNET,
    currency: "ETH",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/arbitrum-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.ARB),
  },
  [Chain.POLYGON]: {
    chain: Chain.POLYGON,
    value: ProtoChain.MATIC,
    name: "Polygon PoS",
    networks: [
      Network.POLYGON_MAINNET,
      Network.POLYGON_MUMBAI,
      Network.POLYGON_AMOY,
    ],
    defaultNetwork: Network.POLYGON_MAINNET,
    currency: "MATIC",
    supportsMempool: true,
    supportsReinforcedTxns: true,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/polygon-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.POLYGON),
  },
  [Chain.POLYGONZKEVM]: {
    chain: Chain.POLYGONZKEVM,
    value: ProtoChain.POLYGONZKEVM,
    name: "Polygon zkEVM",
    networks: [
      Network.POLYGONZKEVM_MAINNET,
      Network.POLYGONZKEVM_TESTNET,
      Network.POLYGONZKEVM_CARDONA,
    ],
    defaultNetwork: Network.POLYGONZKEVM_MAINNET,
    currency: "ETH",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: false,
    docsUrl: "https://docs.alchemy.com/reference/polygon-zkevm-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.POLYGONZKEVM),
  },
  [Chain.OPT]: {
    chain: Chain.OPT,
    value: ProtoChain.OPT,
    name: "Optimism",
    networks: [
      Network.OPT_MAINNET,
      Network.OPT_KOVAN,
      Network.OPT_GOERLI,
      Network.OPT_SEPOLIA,
    ],
    defaultNetwork: Network.OPT_MAINNET,
    currency: "ETH",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/optimism-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.OPT),
  },
  [Chain.SOLANA]: {
    chain: Chain.SOLANA,
    value: ProtoChain.SOLANA,
    name: "Solana",
    networks: [Network.SOLANA_MAINNET, Network.SOLANA_DEVNET],
    defaultNetwork: Network.SOLANA_MAINNET,
    currency: "SOL",
    supportsMempool: false,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/solana-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.SOLANA),
  },
  [Chain.ASTAR]: {
    chain: Chain.ASTAR,
    value: ProtoChain.ASTAR,
    name: "Astar",
    networks: [Network.ASTAR_MAINNET],
    defaultNetwork: Network.ASTAR_MAINNET,
    currency: "ASTR",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: false,
    docsUrl: "https://docs.alchemy.com/reference/astar-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.ASTAR),
  },
  [Chain.STARKNET]: {
    chain: Chain.STARKNET,
    value: ProtoChain.STARKNET,
    name: "Starknet",
    networks: [
      Network.STARKNET_MAINNET,
      Network.STARKNET_GOERLI,
      Network.STARKNET_SEPOLIA,
    ],
    defaultNetwork: Network.STARKNET_MAINNET,
    currency: "ETH",
    supportsMempool: false,
    supportsReinforcedTxns: false,
    supportsWebsockets: false,
    docsUrl: "https://docs.alchemy.com/reference/starknet-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.STARKNET),
  },
  [Chain.BASE]: {
    chain: Chain.BASE,
    value: ProtoChain.BASE,
    name: "Base",
    networks: [Network.BASE_MAINNET, Network.BASE_GOERLI, Network.BASE_SEPOLIA],
    defaultNetwork: Network.BASE_MAINNET,
    currency: "ETH",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/base-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.BASE),
  },
  [Chain.ZKSYNC]: {
    chain: Chain.ZKSYNC,
    value: ProtoChain.ZKSYNC,
    name: "zkSync",
    networks: [Network.ZKSYNC_MAINNET, Network.ZKSYNC_SEPOLIA],
    defaultNetwork: Network.ZKSYNC_MAINNET,
    currency: "ETH",
    supportsMempool: false,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    docsUrl: "https://docs.alchemy.com/reference/zksync-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.ZKSYNC),
  },
  [Chain.ZORA]: {
    chain: Chain.ZORA,
    value: ProtoChain.ZORA,
    name: "Zora",
    networks: [Network.ZORA_MAINNET, Network.ZORA_SEPOLIA],
    defaultNetwork: Network.ZORA_MAINNET,
    currency: "ETH",
    supportsMempool: false,
    supportsReinforcedTxns: false,
    supportsWebsockets: false,
    supportsOnlyAccountAbstraction: true,
    docsUrl: "https://docs.alchemy.com/reference/zora-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.ZORA),
  },
  [Chain.FRAX]: {
    chain: Chain.FRAX,
    value: ProtoChain.FRAX,
    name: "Frax",
    networks: [Network.FRAX_MAINNET, Network.FRAX_SEPOLIA],
    defaultNetwork: Network.FRAX_MAINNET,
    currency: "frxETH",
    supportsMempool: false,
    supportsReinforcedTxns: false,
    supportsWebsockets: false,
    supportsOnlyAccountAbstraction: true,
    docsUrl: "https://docs.alchemy.com/reference/frax-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.FRAX),
  },
  [Chain.BNB]: {
    chain: Chain.BNB,
    value: ProtoChain.BNB,
    name: "BNB Chain",
    networks: [Network.BNB_MAINNET, Network.BNB_TESTNET],
    defaultNetwork: Network.BNB_MAINNET,
    currency: "BNB",
    supportsMempool: false,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    supportsOnlyAccountAbstraction: false,
    docsUrl: "https://docs.alchemy.com/reference/bnb-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.BNB),
  },
  [Chain.AVAX]: {
    chain: Chain.AVAX,
    value: ProtoChain.AVAX,
    name: "Avalanche",
    networks: [Network.AVAX_MAINNET, Network.AVAX_FUJI],
    defaultNetwork: Network.AVAX_MAINNET,
    currency: "AVAX",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    supportsOnlyAccountAbstraction: false,
    docsUrl: "https://docs.alchemy.com/reference/avalanche-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.AVAX),
  },
  [Chain.ARBNOVA]: {
    chain: Chain.ARBNOVA,
    value: ProtoChain.ARBNOVA,
    name: "Arbitrum Nova",
    networks: [Network.ARBNOVA_MAINNET],
    defaultNetwork: Network.ARBNOVA_MAINNET,
    currency: "ETH",
    supportsMempool: true,
    supportsReinforcedTxns: false,
    supportsWebsockets: true,
    supportsOnlyAccountAbstraction: false,
    docsUrl: "https://docs.alchemy.com/reference/arbitrum-nova-api-quickstart",
    order: CHAIN_SORT_ORDER.indexOf(Chain.ARBNOVA),
  },
};

const NETWORK_SORT_ORDER = [
  Network.ETH_MAINNET,
  Network.ETH_SEPOLIA,
  Network.ETH_HOLESKY,
  Network.ETH_GOERLI,
  Network.ETH_RINKEBY,
  Network.ETH_ROPSTEN,
  Network.ETH_KOVAN,
  Network.POLYGON_MAINNET,
  Network.POLYGON_AMOY,
  Network.POLYGON_MUMBAI,
  Network.ARB_MAINNET,
  Network.ARB_SEPOLIA,
  Network.ARB_GOERLI,
  Network.ARB_RINKEBY,
  Network.OPT_MAINNET,
  Network.OPT_SEPOLIA,
  Network.OPT_KOVAN,
  Network.OPT_GOERLI,
  Network.POLYGONZKEVM_MAINNET,
  Network.POLYGONZKEVM_CARDONA,
  Network.POLYGONZKEVM_TESTNET,
  Network.BASE_MAINNET,
  Network.BASE_SEPOLIA,
  Network.BASE_GOERLI,
  Network.STARKNET_MAINNET,
  Network.STARKNET_SEPOLIA,
  Network.STARKNET_GOERLI,
  Network.SOLANA_MAINNET,
  Network.SOLANA_DEVNET,
  Network.ASTAR_MAINNET,
  Network.ZKSYNC_MAINNET,
  Network.ZKSYNC_SEPOLIA,
  Network.ZORA_MAINNET,
  Network.ZORA_SEPOLIA,
  Network.FRAX_MAINNET,
  Network.FRAX_SEPOLIA,
  Network.BNB_MAINNET,
  Network.BNB_TESTNET,
  Network.AVAX_MAINNET,
  Network.AVAX_FUJI,
  Network.ARBNOVA_MAINNET,
];

export const NETWORK_INFO: Record<
  Network,
  {
    network: Network;
    chain: Chain;
    // Display name
    name: string;
    // Subdomain of the API endpoint
    subdomain: string;
    // URL of the public block explorer
    explorerUrl: string;
    // Chain ID of the network as defined by EIP-155 (see chainlist.org)
    // Non-EVM chains may not have a chain ID
    networkChainId?: number;
    // Deprecated networks are visible but can't be selected or have new apps created
    isDeprecated: boolean;
    // Non-public networks don't show up at all unless overridden by a feature flag
    isPublic: boolean;
    // Feature flag that will make network show up even if isPublic is false
    publicOverrideFlag?: WaffleFlag;
    // Network to suggest the user migrate to if deprecated
    deprecatedInFavorOf?: Network;
    // The end of life date Alchemy will not support the network after
    depreciationEndOfLifeDate?: string;
    // The guide url to help with migration for depreciated networks
    depreciationMigrationGuideUrl?: string;
    // List of webhook types that can be created for this network
    supportedWebhookTypes: WebhookType[];
    // Enum used in the alchemy-sdk-js package, if supported (https://github.com/alchemyplatform/alchemy-sdk-js)
    sdkEnum?: string;
    // [Deprecated] Enum used in our protobufs
    protoNetwork: ProtoNetwork;
    // Full domain of the endpoint
    apiDomain: string;
    // Full URL of the GraphQL endpoint, if supported
    graphQLEndpoint?: string;
    // Gas Manager enabled for this network
    supportsGasPolicies: boolean;
    // Network ID used by the gas policy service
    gasPolicyNetwork?: string;
    // Full URL of our official alchemy faucet for this network, if it exists
    faucetUrl?: string;
    // API Key that the public composer uses
    composerApiKey: string;
    // Whether the network is a testnet or not
    isTestNet: boolean;
    // Order to display the network in selectors
    order: number;
  }
> = {
  [Network.ETH_MAINNET]: {
    network: Network.ETH_MAINNET,
    chain: Chain.ETH,
    name: "Ethereum Mainnet",
    subdomain: "eth-mainnet",
    explorerUrl: "https://etherscan.io",
    networkChainId: 1,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "eth-mainnet",
    protoNetwork: ProtoNetwork.ETH_MAINNET,
    sdkEnum: "ETH_MAINNET",
    apiDomain: `eth-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-eth-mainnet.alchemy.com/graphql",
    composerApiKey: "18lHSAeZttzI4wJvHpXlJGHK4ZDNCTCP",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_MAINNET),
  },
  [Network.ETH_ROPSTEN]: {
    network: Network.ETH_ROPSTEN,
    chain: Chain.ETH,
    name: "Ethereum Ropsten",
    subdomain: "eth-ropsten",
    explorerUrl: "https://ropsten.etherscan.io",
    networkChainId: 3,
    isDeprecated: true,
    deprecatedInFavorOf: Network.ETH_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.ETH_ROPSTEN,
    sdkEnum: "ETH_ROPSTEN",
    apiDomain: `eth-ropsten.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "LNhYAtI5bjJambtj5sp_7Zvp9uJTF6AQ",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_ROPSTEN),
  },
  [Network.ETH_RINKEBY]: {
    network: Network.ETH_RINKEBY,
    chain: Chain.ETH,
    name: "Ethereum Rinkeby",
    subdomain: "eth-rinkeby",
    explorerUrl: "https://rinkeby.etherscan.io",
    networkChainId: 4,
    isDeprecated: true,
    deprecatedInFavorOf: Network.ETH_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.ETH_RINKEBY,
    sdkEnum: "ETH_RINKEBY",
    apiDomain: `eth-rinkeby.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "pD6NE41ES8gXalyax7WtS77DmdQPzCnA",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_RINKEBY),
  },
  [Network.ETH_KOVAN]: {
    network: Network.ETH_KOVAN,
    chain: Chain.ETH,
    name: "Ethereum Kovan",
    subdomain: "eth-kovan",
    explorerUrl: "https://kovan.etherscan.io",
    networkChainId: 42,
    isDeprecated: true,
    deprecatedInFavorOf: Network.ETH_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.ETH_KOVAN,
    sdkEnum: "ETH_KOVAN",
    apiDomain: `eth-kovan.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "nGbclHFbxRN2YfO_0tVO0ZPHCO16HUb2",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_KOVAN),
  },
  [Network.ETH_GOERLI]: {
    network: Network.ETH_GOERLI,
    chain: Chain.ETH,
    name: "Ethereum Goerli",
    subdomain: "eth-goerli",
    explorerUrl: "https://goerli.etherscan.io",
    networkChainId: 5,
    isDeprecated: true,
    deprecatedInFavorOf: Network.ETH_SEPOLIA,
    depreciationEndOfLifeDate: "2024-04-01",
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    gasPolicyNetwork: "eth-goerli",
    protoNetwork: ProtoNetwork.ETH_GOERLI,
    sdkEnum: "ETH_GOERLI",
    apiDomain: `eth-goerli.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-eth-goerli.alchemy.com/graphql",
    composerApiKey: "7uIf4QFpJIDY5G0NvGqAQQ9v52V89o6T",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_GOERLI),
  },
  [Network.ETH_SEPOLIA]: {
    network: Network.ETH_SEPOLIA,
    chain: Chain.ETH,
    name: "Ethereum Sepolia",
    subdomain: "eth-sepolia",
    explorerUrl: "https://sepolia.etherscan.io",
    networkChainId: 11155111,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "eth-sepolia",
    protoNetwork: ProtoNetwork.ETH_SEPOLIA,
    sdkEnum: "ETH_SEPOLIA",
    apiDomain: `eth-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-eth-sepolia.alchemy.com/graphql",
    faucetUrl: "https://www.alchemy.com/faucets/ethereum-sepolia",
    composerApiKey: "cumqOgehNNI-_CNcKZtYsdSelVbg0m2z",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_SEPOLIA),
  },
  [Network.ETH_HOLESKY]: {
    network: Network.ETH_HOLESKY,
    chain: Chain.ETH,
    name: "Ethereum Holešky",
    subdomain: "eth-holesky",
    explorerUrl: "https://holesky.etherscan.io",
    networkChainId: 17000,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.EthHolesky,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "eth-holesky",
    protoNetwork: ProtoNetwork.ETH_HOLESKY,
    sdkEnum: "ETH_HOLESKY",
    apiDomain: `eth-holesky.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-eth-holesky.alchemy.com/graphql",
    composerApiKey: "", // TODO
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ETH_HOLESKY),
  },
  [Network.OPT_MAINNET]: {
    network: Network.OPT_MAINNET,
    chain: Chain.OPT,
    name: "Optimism Mainnet",
    subdomain: "opt-mainnet",
    explorerUrl: "https://optimistic.etherscan.io",
    networkChainId: 10,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "opt-mainnet",
    protoNetwork: ProtoNetwork.OPT_MAINNET,
    sdkEnum: "OPT_MAINNET",
    apiDomain: `opt-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-opt-mainnet.alchemy.com/graphql",
    composerApiKey: "aV85fnhU-XcZLD5odRi1pdW71Iq8EMAv",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.OPT_MAINNET),
  },
  [Network.OPT_KOVAN]: {
    network: Network.OPT_KOVAN,
    chain: Chain.OPT,
    name: "Optimism Kovan",
    subdomain: "opt-kovan",
    explorerUrl: "https://kovan-optimistic.etherscan.io",
    networkChainId: 69,
    isDeprecated: true,
    deprecatedInFavorOf: Network.OPT_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.OPT_KOVAN,
    sdkEnum: "OPT_KOVAN",
    apiDomain: `opt-kovan.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "VTy5VKakEVF4yeg0U1IpA8z1-cblA6lx",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.OPT_KOVAN),
  },
  [Network.OPT_GOERLI]: {
    network: Network.OPT_GOERLI,
    chain: Chain.OPT,
    name: "Optimism Goerli",
    subdomain: "opt-goerli",
    explorerUrl: "https://goerli-optimism.etherscan.io",
    networkChainId: 420,
    isDeprecated: true,
    deprecatedInFavorOf: Network.OPT_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    gasPolicyNetwork: "opt-goerli",
    protoNetwork: ProtoNetwork.OPT_GOERLI,
    sdkEnum: "OPT_GOERLI",
    apiDomain: `opt-goerli.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-opt-goerli.alchemy.com/graphql",
    composerApiKey: "In5QdUuCOOlIIrqJML62ST2TafnGK9sq",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.OPT_GOERLI),
  },
  [Network.OPT_SEPOLIA]: {
    network: Network.OPT_SEPOLIA,
    chain: Chain.OPT,
    name: "Optimism Sepolia",
    subdomain: "opt-sepolia",
    explorerUrl: "https://sepolia-optimism.etherscan.io",
    networkChainId: 11155420,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "opt-sepolia",
    protoNetwork: ProtoNetwork.OPT_SEPOLIA,
    sdkEnum: "OPT_SEPOLIA",
    apiDomain: `opt-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-opt-sepolia.alchemy.com/graphql",
    faucetUrl: "https://www.alchemy.com/faucets/optimism-sepolia",
    composerApiKey: "HxEdMld8t-OKLwXCZc_COleSrepljtyW",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.OPT_SEPOLIA),
  },
  [Network.ARB_MAINNET]: {
    network: Network.ARB_MAINNET,
    chain: Chain.ARB,
    name: "Arbitrum Mainnet",
    subdomain: "arb-mainnet",
    explorerUrl: "https://explorer.arbitrum.io",
    networkChainId: 42161,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "arb-mainnet",
    protoNetwork: ProtoNetwork.ARB_MAINNET,
    sdkEnum: "ARB_MAINNET",
    apiDomain: `arb-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-arb-mainnet.alchemy.com/graphql",
    composerApiKey: "0cJFSUjJmVgWznuaHn0XeJijtsAumO_C",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.ARB_MAINNET),
  },
  [Network.ARB_RINKEBY]: {
    network: Network.ARB_RINKEBY,
    chain: Chain.ARB,
    name: "Arbitrum Rinkeby",
    subdomain: "arb-rinkeby",
    explorerUrl: "https://rinkeby-explorer.arbitrum.io",
    networkChainId: 421611,
    isDeprecated: true,
    deprecatedInFavorOf: Network.ARB_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.ARB_RINKEBY,
    sdkEnum: "ARB_RINKEBY",
    apiDomain: `arb-rinkeby.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "dAnEgTX45tYXUXfNwtnXGRpJ-YiDBL7d",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ARB_RINKEBY),
  },
  [Network.ARB_GOERLI]: {
    network: Network.ARB_GOERLI,
    chain: Chain.ARB,
    name: "Arbitrum Goerli",
    subdomain: "arb-goerli",
    explorerUrl: "https://goerli-explorer.arbitrum.io",
    networkChainId: 421613,
    isDeprecated: true,
    deprecatedInFavorOf: Network.ARB_SEPOLIA,
    depreciationEndOfLifeDate: "2024-03-18",
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/blog/arbitrum-goerli-testnet-deprecation",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    gasPolicyNetwork: "arb-goerli",
    protoNetwork: ProtoNetwork.ARB_GOERLI,
    sdkEnum: "ARB_GOERLI",
    apiDomain: `arb-goerli.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-arb-goerli.alchemy.com/graphql",
    composerApiKey: "-yClFOTcb3Ls_31uSXpGxf7Vg2RuZaKl",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ARB_GOERLI),
  },
  [Network.ARB_SEPOLIA]: {
    network: Network.ARB_SEPOLIA,
    chain: Chain.ARB,
    name: "Arbitrum Sepolia",
    subdomain: "arb-sepolia",
    explorerUrl: "https://sepolia-explorer.arbitrum.io/",
    networkChainId: 421614,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "arb-sepolia",
    protoNetwork: ProtoNetwork.ARB_SEPOLIA,
    sdkEnum: "ARB_SEPOLIA",
    apiDomain: `arb-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-arb-sepolia.alchemy.com/graphql",
    faucetUrl: "https://www.alchemy.com/faucets/arbitrum-sepolia",
    composerApiKey: "DFdhhmvHUEHp9iFz220W_-nWMLQlSRIW",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ARB_SEPOLIA),
  },
  [Network.POLYGON_MAINNET]: {
    network: Network.POLYGON_MAINNET,
    chain: Chain.POLYGON,
    name: "Polygon Mainnet",
    subdomain: "polygon-mainnet",
    explorerUrl: "https://polygonscan.com",
    networkChainId: 137,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.NFT_METADATA_UPDATE,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "polygon-mainnet",
    protoNetwork: ProtoNetwork.MATIC_MAINNET,
    sdkEnum: "MATIC_MAINNET",
    apiDomain: `polygon-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-polygon-mainnet.alchemy.com/graphql",
    composerApiKey: "GHDvgOcLW8-U_wwk5er-Lryn6jUjGz6V",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.POLYGON_MAINNET),
  },
  [Network.POLYGON_MUMBAI]: {
    network: Network.POLYGON_MUMBAI,
    chain: Chain.POLYGON,
    name: "Polygon Mumbai",
    subdomain: "polygon-mumbai",
    explorerUrl: "https://mumbai.polygonscan.com",
    networkChainId: 80001,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "polygon-mumbai",
    protoNetwork: ProtoNetwork.MATIC_MUMBAI,
    sdkEnum: "MATIC_MUMBAI",
    apiDomain: `polygon-mumbai.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-polygon-mumbai.alchemy.com/graphql",
    composerApiKey: "73DMyhAzFRUlVTxsYlpNtW4FuHHwXJto",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.POLYGON_MUMBAI),
  },
  [Network.POLYGON_AMOY]: {
    network: Network.POLYGON_AMOY,
    chain: Chain.POLYGON,
    name: "Polygon Amoy",
    subdomain: "polygon-amoy",
    explorerUrl: "https://www.oklink.com/amoy",
    networkChainId: 80002,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.MINED_TRANSACTION,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.NFT_ACTIVITY,
      WebhookType.GRAPHQL,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "polygon-amoy",
    protoNetwork: ProtoNetwork.MATIC_AMOY,
    sdkEnum: "MATIC_AMOY",
    apiDomain: `polygon-amoy.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-polygon-amoy.alchemy.com/graphql",
    faucetUrl: "https://www.alchemy.com/faucets/polygon-amoy",
    composerApiKey: "piIMYNItqfnYCTGzCdyhKd9xl2EukNy_",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.POLYGON_AMOY),
  },
  [Network.SOLANA_MAINNET]: {
    network: Network.SOLANA_MAINNET,
    chain: Chain.SOLANA,
    name: "Solana Mainnet",
    subdomain: "solana-mainnet",
    explorerUrl: "https://explorer.solana.com",
    networkChainId: undefined, // Not EVM
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    sdkEnum: "",
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.SOLANA_MAINNET,
    apiDomain: `solana-mainnet.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "e4pk9995fsk63zjg8pjw2863v86w1zdv",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.SOLANA_MAINNET),
  },
  [Network.SOLANA_DEVNET]: {
    network: Network.SOLANA_DEVNET,
    chain: Chain.SOLANA,
    name: "Solana Devnet",
    subdomain: "solana-devnet",
    explorerUrl: "https://explorer.solana.com/?cluster=devnet",
    networkChainId: undefined, // Not EVM
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    sdkEnum: "",
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.SOLANA_DEVNET,
    apiDomain: `solana-devnet.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "pvwxmv5hac6uly99lqxlq183lzobddo7",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.SOLANA_DEVNET),
  },
  [Network.ASTAR_MAINNET]: {
    network: Network.ASTAR_MAINNET,
    chain: Chain.ASTAR,
    name: "Astar Mainnet",
    subdomain: "astar-mainnet",
    explorerUrl: "https://astar.subscan.io/",
    networkChainId: 592,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.ASTAR_MAINNET,
    sdkEnum: "ASTAR_MAINNET",
    apiDomain: `astar-mainnet.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "Z1zXtfUMdcTOzIHJ2zAy5WBeZjI1FGXb",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.ASTAR_MAINNET),
  },
  [Network.POLYGONZKEVM_MAINNET]: {
    network: Network.POLYGONZKEVM_MAINNET,
    chain: Chain.POLYGONZKEVM,
    name: "Polygon zkEVM Mainnet",
    subdomain: "polygonzkevm-mainnet",
    explorerUrl: "https://explorer.public.zkevm-test.net/",
    networkChainId: 1101,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.POLYGONZKEVM_MAINNET,
    sdkEnum: "", // L1 so no SDK support
    apiDomain: `polygonzkevm-mainnet.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "WeJpqzUFH2ZtCT-DlvhX6nvbqTSsqi0y",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.POLYGONZKEVM_MAINNET),
  },
  [Network.POLYGONZKEVM_TESTNET]: {
    network: Network.POLYGONZKEVM_TESTNET,
    chain: Chain.POLYGONZKEVM,
    name: "Polygon zkEVM Testnet",
    subdomain: "polygonzkevm-testnet",
    explorerUrl: "https://testnet-zkevm.polygonscan.com/",
    networkChainId: 1442,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.POLYGONZKEVM_TESTNET,
    sdkEnum: "", // L1 so no SDK support
    apiDomain: `polygonzkevm-testnet.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "d9BlbfN90YpqqPgt52n2e2MIG-42-ctx",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.POLYGONZKEVM_TESTNET),
  },
  [Network.POLYGONZKEVM_CARDONA]: {
    network: Network.POLYGONZKEVM_CARDONA,
    chain: Chain.POLYGONZKEVM,
    name: "Polygon zkEVM Cardona",
    subdomain: "polygonzkevm-cardona",
    explorerUrl: "https://cardona-zkevm.polygonscan.com/",
    networkChainId: 2442,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.POLYGONZKEVM_CARDONA,
    sdkEnum: "", // L1 so no SDK support
    apiDomain: `polygonzkevm-cardona.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "Hcc2xkA0oQF8ELceZQ67LCpNHWxsXkVS",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.POLYGONZKEVM_CARDONA),
  },
  [Network.STARKNET_MAINNET]: {
    network: Network.STARKNET_MAINNET,
    chain: Chain.STARKNET,
    name: "Starknet Mainnet",
    subdomain: "starknet-mainnet",
    explorerUrl: "https://voyager.online/",
    networkChainId: undefined, // non-EVM, but has chain id 0x534e5f4d41494e
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.STARKNET_MAINNET,
    sdkEnum: "", // no SDK support
    apiDomain: `starknet-mainnet.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "RJoejub1z1LwMZWnA75Q0jg5tl__F2YK",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.STARKNET_MAINNET),
  },
  [Network.STARKNET_GOERLI]: {
    network: Network.STARKNET_GOERLI,
    chain: Chain.STARKNET,
    name: "Starknet Goerli",
    subdomain: "starknet-goerli",
    explorerUrl: "https://goerli.voyager.online/",
    networkChainId: undefined, // non-EVM, but has chain id 0x534e5f474f45524c49
    isDeprecated: false,
    deprecatedInFavorOf: Network.STARKNET_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.STARKNET_GOERLI,
    sdkEnum: "", // no SDK support
    apiDomain: `starknet-goerli.g.${API_DOMAIN_SUFFIX}`,
    composerApiKey: "qKIz8HNu4M7RlOQNjdSQHuA6F57Ag88h",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.STARKNET_GOERLI),
  },
  [Network.STARKNET_SEPOLIA]: {
    network: Network.STARKNET_SEPOLIA,
    chain: Chain.STARKNET,
    name: "Starknet Sepolia",
    subdomain: "starknet-sepolia",
    explorerUrl: "https://sepolia.voyager.online/",
    networkChainId: undefined, // non-EVM
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.STARKNET_SEPOLIA,
    sdkEnum: "", // no SDK support
    apiDomain: `starknet-sepolia.g.${API_DOMAIN_SUFFIX}`,
    faucetUrl: "https://www.alchemy.com/faucets/starknet-sepolia",
    composerApiKey: "OTDWsjjq7mq8D3kr0xii6G2F4IdCqmCY",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.STARKNET_SEPOLIA),
  },
  [Network.BASE_MAINNET]: {
    network: Network.BASE_MAINNET,
    chain: Chain.BASE,
    name: "Base Mainnet",
    subdomain: "base-mainnet",
    explorerUrl: "https://basescan.org",
    networkChainId: 8453,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.GRAPHQL,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.MINED_TRANSACTION,
      WebhookType.NFT_ACTIVITY,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "base-mainnet",
    protoNetwork: ProtoNetwork.BASE_MAINNET,
    sdkEnum: "", // no SDK support
    apiDomain: `base-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-base-mainnet.alchemy.com/graphql",
    composerApiKey: "09EaX5cl3H3ecqY3VLzzzRotK2JlV_8S",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.BASE_MAINNET),
  },
  [Network.BASE_GOERLI]: {
    network: Network.BASE_GOERLI,
    chain: Chain.BASE,
    name: "Base Goerli",
    subdomain: "base-goerli",
    explorerUrl: "https://goerli.basescan.org",
    networkChainId: 84531,
    isDeprecated: true,
    deprecatedInFavorOf: Network.BASE_SEPOLIA,
    depreciationMigrationGuideUrl:
      "https://www.alchemy.com/overviews/migrate-to-sepolia",
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    gasPolicyNetwork: "base-goerli",
    protoNetwork: ProtoNetwork.BASE_GOERLI,
    sdkEnum: "", // no SDK support
    apiDomain: `base-goerli.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-base-goerli.alchemy.com/graphql",
    composerApiKey: "D6FkDqx9BRZ0NAg4c6aPF5tom5x-ZLZO",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.BASE_GOERLI),
  },
  [Network.BASE_SEPOLIA]: {
    network: Network.BASE_SEPOLIA,
    chain: Chain.BASE,
    name: "Base Sepolia",
    subdomain: "base-sepolia",
    explorerUrl: "https://sepolia.basescan.org",
    networkChainId: 84532,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.GRAPHQL,
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.MINED_TRANSACTION,
      WebhookType.NFT_ACTIVITY,
    ],
    supportsGasPolicies: true,
    gasPolicyNetwork: "base-sepolia",
    protoNetwork: ProtoNetwork.BASE_SEPOLIA,
    sdkEnum: "", // no SDK support
    apiDomain: `base-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-base-sepolia.alchemy.com/graphql",
    faucetUrl: "https://www.alchemy.com/faucets/base-sepolia",
    composerApiKey: "oledVAHOq-6gKGwEy9-4a9E4C8FIXUs3",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.BASE_SEPOLIA),
  },
  [Network.ZKSYNC_MAINNET]: {
    network: Network.ZKSYNC_MAINNET,
    chain: Chain.ZKSYNC,
    name: "ZKSync Mainnet",
    subdomain: "zksync-mainnet",
    explorerUrl: "https://explorer.zksync.io",
    networkChainId: 324,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.zkSync,
    supportedWebhookTypes: [
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.MINED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.GRAPHQL,
      WebhookType.NFT_ACTIVITY,
    ],
    supportsGasPolicies: false,
    gasPolicyNetwork: undefined,
    protoNetwork: ProtoNetwork.ZKSYNC_MAINNET,
    sdkEnum: undefined,
    apiDomain: `zksync-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "79rTmReVL4hPYJqnhhFYHiy3ieSKkuTK",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.ZKSYNC_MAINNET),
  },
  [Network.ZKSYNC_SEPOLIA]: {
    network: Network.ZKSYNC_SEPOLIA,
    chain: Chain.ZKSYNC,
    name: "ZKSync Sepolia",
    subdomain: "zksync-sepolia",
    explorerUrl: "https://sepolia.explorer.zksync.io/",
    networkChainId: 300,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.zkSync,
    supportedWebhookTypes: [
      WebhookType.DROPPED_TRANSACTION,
      WebhookType.MINED_TRANSACTION,
      WebhookType.ADDRESS_ACTIVITY,
      WebhookType.GRAPHQL,
      WebhookType.NFT_ACTIVITY,
    ],
    supportsGasPolicies: false,
    gasPolicyNetwork: undefined,
    protoNetwork: ProtoNetwork.ZKSYNC_SEPOLIA,
    sdkEnum: undefined,
    apiDomain: `zksync-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "cXGOoNGVovSWsksacCi9gy1uumtleJqv",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ZKSYNC_SEPOLIA),
  },
  [Network.ZORA_MAINNET]: {
    network: Network.ZORA_MAINNET,
    chain: Chain.ZORA,
    name: "Zora Mainnet",
    subdomain: "zora-mainnet",
    explorerUrl: "https://explorer.zora.energy",
    networkChainId: 7777777,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "zora-mainnet",
    protoNetwork: ProtoNetwork.ZORA_MAINNET,
    sdkEnum: undefined,
    apiDomain: `zora-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "i6AxQDSyHeeUtVRKbZ7doVQFudvUTq2X",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.ZORA_MAINNET),
  },
  [Network.ZORA_SEPOLIA]: {
    network: Network.ZORA_SEPOLIA,
    chain: Chain.ZORA,
    name: "Zora Sepolia",
    subdomain: "zora-sepolia",
    explorerUrl: "https://sepolia.explorer.zora.energy",
    networkChainId: 999999999,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "zora-sepolia",
    protoNetwork: ProtoNetwork.ZORA_SEPOLIA,
    sdkEnum: undefined,
    apiDomain: `zora-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "BYCmaMzxY3yXfy2aKu_dt2U5sbS69D9A",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.ZORA_SEPOLIA),
  },
  [Network.FRAX_MAINNET]: {
    network: Network.FRAX_MAINNET,
    chain: Chain.FRAX,
    name: "Frax Mainnet",
    subdomain: "frax-mainnet",
    explorerUrl: "https://fraxscan.com",
    networkChainId: 252,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "frax-mainnet",
    protoNetwork: ProtoNetwork.FRAX_MAINNET,
    sdkEnum: undefined,
    apiDomain: `frax-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "4Em7f-uifhAs9gkA-upE5T4LNImBMS0J",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.FRAX_MAINNET),
  },
  [Network.FRAX_SEPOLIA]: {
    network: Network.FRAX_SEPOLIA,
    chain: Chain.FRAX,
    name: "Frax Sepolia",
    subdomain: "frax-sepolia",
    explorerUrl: "https://explorer.testnet-sepolia.frax.com",
    networkChainId: 2522,
    isDeprecated: false,
    isPublic: true,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "frax-sepolia",
    protoNetwork: ProtoNetwork.FRAX_MAINNET,
    sdkEnum: undefined,
    apiDomain: `frax-sepolia.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "JLulVUeZjzf7zMIydZ2HMAfZAslVByX8",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.FRAX_MAINNET),
  },
  [Network.BNB_MAINNET]: {
    network: Network.BNB_MAINNET,
    chain: Chain.BNB,
    name: "BNB Chain",
    subdomain: "bnb-mainnet",
    explorerUrl: "https://bscscan.com",
    networkChainId: 56,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.BNBMainnet,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.BNB_MAINNET,
    sdkEnum: undefined,
    apiDomain: `bnb-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "BlDbnNkd8X76A0Q5qS-Y10YD-b3EgvFB",
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.BNB_MAINNET),
  },
  [Network.BNB_TESTNET]: {
    network: Network.BNB_TESTNET,
    chain: Chain.BNB,
    name: "BNB Testnet",
    subdomain: "bnb-testnet",
    explorerUrl: "https://testnet.bscscan.com/",
    networkChainId: 97,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.BNBTestnet,
    supportedWebhookTypes: [],
    supportsGasPolicies: false,
    protoNetwork: ProtoNetwork.BNB_TESTNET,
    sdkEnum: undefined,
    apiDomain: `bnb-testnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: undefined,
    composerApiKey: "lBCf_8WSnyhWQHyKalP8GGjjI6mDlg4O",
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.BNB_TESTNET),
  },
  [Network.AVAX_MAINNET]: {
    network: Network.AVAX_MAINNET,
    chain: Chain.AVAX,
    name: "Avalanche Mainnet",
    subdomain: "avax-mainnet",
    explorerUrl: "https://subnets.avax.network/c-chain",
    networkChainId: 43114,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.Avax,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "avax-mainnet",
    protoNetwork: ProtoNetwork.AVAX_MAINNET,
    sdkEnum: undefined,
    apiDomain: `avax-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-avax-mainnet.alchemy.com/graphql",
    composerApiKey: "", // TODO
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.AVAX_MAINNET),
  },
  [Network.AVAX_FUJI]: {
    network: Network.AVAX_FUJI,
    chain: Chain.AVAX,
    name: "Avalanche Testnet",
    subdomain: "avax-testnet",
    explorerUrl: "https://subnets-test.avax.network/c-chain",
    networkChainId: 43113,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.Avax,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "avax-fuji",
    protoNetwork: ProtoNetwork.AVAX_FUJI,
    sdkEnum: undefined,
    apiDomain: `avax-fuji.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-avax-fuji.alchemy.com/graphql",
    composerApiKey: "", // TODO
    isTestNet: true,
    order: NETWORK_SORT_ORDER.indexOf(Network.AVAX_FUJI),
  },
  [Network.ARBNOVA_MAINNET]: {
    network: Network.ARBNOVA_MAINNET,
    chain: Chain.ARBNOVA,
    name: "Arbitrum Nova Mainnet",
    subdomain: "arbnova-mainnet",
    explorerUrl: "https://nova.arbiscan.io/",
    networkChainId: 42170,
    isDeprecated: false,
    isPublic: false,
    publicOverrideFlag: WaffleFlag.ArbNova,
    supportedWebhookTypes: [],
    supportsGasPolicies: true,
    gasPolicyNetwork: "arbnova-mainnet",
    protoNetwork: ProtoNetwork.ARBNOVA_MAINNET,
    sdkEnum: undefined,
    apiDomain: `arbnova-mainnet.g.${API_DOMAIN_SUFFIX}`,
    graphQLEndpoint: "https://graphql-arbnova-mainnet.alchemy.com/graphql",
    composerApiKey: "", // TODO
    isTestNet: false,
    order: NETWORK_SORT_ORDER.indexOf(Network.ARBNOVA_MAINNET),
  },
};

export const CHAINS = sortBy(Object.values(CHAIN_INFO), "order").map(
  (c) => c.chain,
);

export const NETWORKS = sortBy(Object.values(NETWORK_INFO), "order").map(
  (info) => info.network,
);

export const TRACE_COMPOSER_API_KEY = "dAnEgTX45tYXUXfNwtnXGRpJ-YiDBL7d";

export enum SignupSource {
  TWITTER = 1,
  TEAMMATE = 2,
  SEARCH_ENGINE = 3,
  TUTORIAL = 4,
  OTHER = 999,
}

export enum OnDemandActionType {
  UNSPECIFIED = 0,
  ALERT = 1,
  STOPPAGE = 2,
}

export enum OnDemandThresholdUnit {
  UNSPECIFIED = 0,
  CU = 1,
  DOLLAR = 2,
}

export enum LocalStorageKeys {
  AppListSort = "appListSort",
  AppSortOrder = "appSortOrder",
}
